import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatCurrency, formatDateByType } from "../../../libs/utils";
import { ConfirmGridModal, createRemoveRowColumn } from "../../../molecules/ConfirmGridModal";
import { Invoice } from "./types";

type SendInvoiceRow = Invoice & {
  tenantDetails?: { tenantNameEN?: string };
  _id?: string;
  unitCode?: string;
};

type SendInvoicesConfirmProps = {
  invoices: SendInvoiceRow[];
  onConfirm: (pdcIds: string[]) => void | Promise<unknown>;
  onClose: () => void;
  isLoading?: boolean;
};

const getRowId = (inv: SendInvoiceRow) => String(inv.pdcId ?? inv._id ?? "");

export const SendInvoicesConfirm = (props: SendInvoicesConfirmProps) => {
  const { invoices, onConfirm, onClose, isLoading } = props;

  const [rows, setRows] = useState<SendInvoiceRow[]>(() => invoices);

  useEffect(() => {
    setRows(invoices);
  }, [invoices]);

  const removeRow = useCallback((rowId: string) => {
    setRows((prev) => prev.filter((r) => getRowId(r) !== rowId));
  }, []);

  const data = useMemo(
    () =>
      rows.map((inv) => ({
        rowId: getRowId(inv),
        invoiceNumber: inv.invoiceNumber ?? "",
        dueDate: inv.dueDate ?? "",
        propertyName: inv.propertyDetails?.propertyName ?? "",
        tenantName: inv.tenantDetails?.tenantNameEN ?? "",
        unitCode: inv.unitCode ?? inv.propertyDetails?.unitNumber ?? "",
        totalAmount: inv.totalAmount,
      })),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "invoiceNumber",
        headerName: "Invoice no.",
        flex: 0.9,
        minWidth: 100,
        maxWidth: 124,
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        flex: 1,
        maxWidth: 140,
        valueFormatter: ({ value }) => formatDateByType(value),
      },
      { field: "propertyName", headerName: "Property", flex: 1, minWidth: 140 },
      { field: "tenantName", headerName: "Tenant Name", flex: 1.35, minWidth: 185 },
      { field: "unitCode", headerName: "Unit Code", flex: 1, maxWidth: 130 },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        flex: 1,
        minWidth: 160,
        maxWidth: 220,
        type: "rightAligned",
        valueFormatter: ({ value }) => formatCurrency(value),
        cellStyle: { textAlign: "right", overflow: "visible", borderRight: "none" },
      },
      createRemoveRowColumn({
        field: "rowId",
        onRemove: removeRow,
        ariaLabel: "Remove from send list",
      }),
    ],
    [removeRow],
  );

  const handleConfirm = () => {
    const ids = rows.map((r) => getRowId(r)).filter(Boolean);
    void onConfirm(ids);
  };

  return (
    <ConfirmGridModal>
      {rows.length > 0 && (
        <ConfirmGridModal.SubText>
          The {rows.length} invoice{rows.length === 1 ? "" : "s"} below will be sent to tenants. Remove any you do not
          want to include before confirming.
        </ConfirmGridModal.SubText>
      )}
      <ConfirmGridModal.Grid
        columns={columns}
        data={data}
        isEmpty={rows.length === 0}
        emptyMessage="No invoices left in this list. Close the dialog and select again."
      />
      <ConfirmGridModal.Actions
        align="space-between"
        style={{ marginTop: "2.5rem" }}
        primaryButton={{
          label: "Confirm and Send",
          onClick: handleConfirm,
          loading: isLoading,
          disabled: isLoading || rows.length === 0,
        }}
        secondaryButton={{
          label: "Cancel",
          onClick: onClose,
          disabled: isLoading,
        }}
      />
    </ConfirmGridModal>
  );
};
